import { AfterViewInit, Component, Inject, OnInit, Optional } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { CaptureReferralIdFromParamUseCase } from '@app/common/headers/application/use-cases/capture-referral-id-from-param.use-case';
import { filter } from 'rxjs/operators';
import { HeaderRepository } from '@ui/features/headers/infrastructure/header.repository';
import { PlatformBrowserService } from './core/services/platform-browser.service';
import { MatIconRegisterService } from '@app/core/services/http-interceptors/mat-icon-register.service';
import { FirebaseMessagingService } from './firebase/firebase-messaging.service';
import { ErrorTrustedService } from './exceptions/services/error-trusted.service';
import { MatDialog } from '@angular/material/dialog';
import { DialogErrorRedirectComponent } from './core/components/dialog-error-redirect/dialog-error-redirect.component';
import { CrispService } from './core/services/crisp.service';
import { ErrorReporterService } from './exceptions/services/error-reporter.service';
import { BREADCRUMB_TRACKER_PORT } from './exceptions/reporting/breadcrumbs/breadcrumb-tracker.token';
import { IBreadcrumbTrackerService } from '@app/common/observability/domain/services/breadcrumb-tracker.service';
import { TokenStorage } from './get-token';
import { TokenUpdateService } from './dashboard/services/token-update.service';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    standalone: false,
})
export class AppComponent implements AfterViewInit, OnInit {
    public checkErrorTrusted: boolean = false;
    private isCrispInitializedOnApp: boolean = false;
    private hasReportedAppBootstrapBreadcrumb: boolean = false;
    private hasReportedNavigationReadyBreadcrumb: boolean = false;
    private previousRoute: string = '';
    private readonly captureReferralIdFromParamUseCase: CaptureReferralIdFromParamUseCase =
        new CaptureReferralIdFromParamUseCase(new HeaderRepository());

    public constructor(
        private crispService: CrispService,
        private router: Router,
        private activatedRoute: ActivatedRoute,
        private matIconRegisterService: MatIconRegisterService,
        private browserService: PlatformBrowserService,
        private firebaseMessagingService: FirebaseMessagingService,
        private errorTrustedService: ErrorTrustedService,
        private matDialog: MatDialog,
        private errorReporterService: ErrorReporterService,
        private tokenUpdateService: TokenUpdateService,
        @Optional()
        @Inject(BREADCRUMB_TRACKER_PORT)
        private breadcrumbTrackerService?: IBreadcrumbTrackerService,
    ) {
        if (this.shouldInitializeFirebaseMessaging()) {
            this.runWhenBrowserIdle((): void => {
                this.firebaseMessagingService.listen();
            }, 2000);
        }
        if (this.browserService.isBrowser) {
            document.documentElement.classList.add('is-browser');
            if (this.activatedRoute.snapshot.queryParams.refId) {
                this.captureReferralIdFromParamUseCase.execute(
                    this.activatedRoute.snapshot.queryParams.refId,
                );
            }

            this.tokenUpdateService.tokenUpdated$.subscribe((userId: string | null): void => {
                if (userId === null) {
                    this.crispService.logOutCrisp();
                }
            });

            this.router.events
                .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
                .subscribe((event: NavigationEnd): void => {
                    if (this.activatedRoute.snapshot.queryParams.show_referral_id) {
                        localStorage.setItem(
                            'show_referral_id',
                            this.activatedRoute.snapshot.queryParams.show_referral_id,
                        );
                    }

                    if (localStorage.getItem('show_referral_id')) {
                        let next_query_params: { [property: string]: any } = {
                            ...this.activatedRoute.snapshot.queryParams,
                        };
                        delete next_query_params.show_referral_id;
                        next_query_params.refId = localStorage.getItem('show_referral_id');
                        this.router
                            .navigate([this.router.url.split('?')[0]], {
                                queryParams: next_query_params,
                            })
                            .catch(() => undefined);
                    }

                    this.initializeCrispIfNeeded();
                    this.crispService.syncForCurrentContext(event.urlAfterRedirects || event.url);
                    this.trackNavigation(event.urlAfterRedirects || event.url);
                });
        }
    }

    public ngOnInit(): void {
        if (!this.browserService.isBrowser) {
            return;
        }

        this.initializeIconSet();
        this.errorReporterService.start();

        if (!this.hasReportedAppBootstrapBreadcrumb && this.breadcrumbTrackerService) {
            this.breadcrumbTrackerService.appBootstrapCompleted({
                hasAccessToken: Boolean(TokenStorage.getToken()),
            });
            this.hasReportedAppBootstrapBreadcrumb = true;
        }

        this.initializeCrispIfNeeded();
    }

    private initializeIconSet(): void {
        this.matIconRegisterService.addSvgIconSet('assets/solido-icons-v4.svg');
    }

    private trackNavigation(currentRoute: string): void {
        if (!this.breadcrumbTrackerService) {
            this.previousRoute = currentRoute;

            return;
        }

        if (!this.hasReportedNavigationReadyBreadcrumb) {
            this.breadcrumbTrackerService.navigationReady({ to: currentRoute });
            this.hasReportedNavigationReadyBreadcrumb = true;
            this.previousRoute = currentRoute;

            return;
        }

        if (this.previousRoute !== currentRoute) {
            this.breadcrumbTrackerService.navigationRouteChanged({
                from: this.previousRoute || 'unknown',
                to: currentRoute,
            });
        }

        this.previousRoute = currentRoute;
    }

    private initializeCrispIfNeeded(): void {
        if (this.isCrispInitializedOnApp || !this.shouldInitializeCrisp()) {
            return;
        }

        this.runWhenBrowserIdle(
            (): void => {
                if (this.isCrispInitializedOnApp || !this.shouldInitializeCrisp()) {
                    return;
                }

                this.crispService.initializeCrisp();
                this.isCrispInitializedOnApp = true;
            },
            2200,
            1200,
        );
    }

    private runWhenBrowserIdle(
        callback: () => void,
        idleTimeout: number,
        fallbackTimeout: number = 800,
    ): void {
        const globalWindow: Window & typeof globalThis = window;

        if ('requestIdleCallback' in globalWindow) {
            globalWindow.requestIdleCallback(callback, { timeout: idleTimeout });

            return;
        }

        globalThis.setTimeout(callback, fallbackTimeout);
    }

    private shouldInitializeCrisp(): boolean {
        const currentPath: string = window.location.pathname;

        return !currentPath.startsWith('/wiki/') && !currentPath.startsWith('/t/');
    }

    private shouldInitializeFirebaseMessaging(): boolean {
        if (!this.browserService.isBrowser) {
            return false;
        }

        const currentPath: string = window.location.pathname;

        if (this.isAuthRoute(currentPath)) {
            return false;
        }

        return (
            currentPath.startsWith('/my/') ||
            currentPath.startsWith('/cobrar/') ||
            currentPath.startsWith('/dashboard') ||
            currentPath.startsWith('/transactions') ||
            currentPath.startsWith('/es/my/') ||
            currentPath.startsWith('/es/cobrar/') ||
            currentPath.startsWith('/en/my/')
        );
    }

    private isAuthRoute(pathname: string): boolean {
        return /\/(login|signup|forgotpass|resetpassword|message)\b/.test(pathname);
    }

    public ngAfterViewInit(): void {
        if (!this.browserService.isBrowser) {
            return;
        }
        this.errorTrustedService.isError.subscribe(() => {
            if (this.checkErrorTrusted || !this.browserService.isBrowser) {
                return;
            }
            this.matDialog.open(DialogErrorRedirectComponent, {
                width: '600px',
                panelClass: 'sol-card-dialog',
                autoFocus: false,
            });
            this.checkErrorTrusted = true;
        });
    }

    public onActivate(): void {
        if (this.browserService.isBrowser) {
            window.scroll(0, 0);
        }
    }
}
